import { Suspense } from 'react';
import { Link, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { Box, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';

import OverLayLoader from '../components/OverLayLoader';
import ReplacementsView from './subviews/dictionaries/ReplacementsView';
import RelatedCodeView from './subviews/dictionaries/RelatedCodeView';
import MappedCodeView from './subviews/dictionaries/MappedCodeView';
import ComplexCodeView from './subviews/dictionaries/ComplexCodeView';
import WordsSettingsView from './subviews/dictionaries/WordsSettingsView';
import DownloadsView from './subviews/dictionaries/DownloadsView';
import AddKeywordsView from './actions_views/AddKeywordsView';
import AddReplacementView from './actions_views/AddReplacementView';
import AddRelatedLinkView from './actions_views/AddRelatedLinkView';
import AddComplexLinkView from './actions_views/AddComplexLinkView';
import AddMappedLinkView from './actions_views/AddMappedLinkView';
import RoleRoute from '../components/route/RoleRoute/RoleRoute';
import { useAuth } from '../context/auth/AuthContext';
import PATH from '../helpers/routes.json';

const DictionariesView = () => {
  const { t } = useTranslation(['common']);
  const { pathname } = useLocation();
  const { user } = useAuth();

  const visibleTabs = Object.keys(PATH.dictionaries.tabs).filter(
    tab => !PATH.dictionaries.adminOnlyTabs.includes(tab) || user?.is_superuser,
  );

  const tabsValue = pathname.startsWith(`${PATH.dictionaries.addWords}/`)
    ? PATH.dictionaries.tabs.words
    : pathname.startsWith(`${PATH.dictionaries.addReplacement}/`)
      ? PATH.dictionaries.tabs.replacements
      : pathname.startsWith(`${PATH.dictionaries.addRelatedLink}/`)
        ? PATH.dictionaries.tabs.related_code
        : pathname.startsWith(`${PATH.dictionaries.addComplexLink}/`)
          ? PATH.dictionaries.tabs.complex_code
          : pathname.startsWith(`${PATH.dictionaries.addMappedLink}/`)
            ? PATH.dictionaries.tabs.mapped_code
            : visibleTabs.some(tab => PATH.dictionaries.tabs[tab] === pathname)
              ? pathname
              : false;

  return (
    <Box>
      <Tabs
        value={tabsValue}
        textColor="primary"
        indicatorColor="primary"
        aria-label="Dictionaries"
      >
        {visibleTabs.map(tab => (
          <Tab
            key={tab}
            value={PATH.dictionaries.tabs[tab]}
            label={t(`pages.dictionaries.tabs.${tab}`)}
            component={Link}
            to={PATH.dictionaries.tabs[tab]}
          />
        ))}
      </Tabs>

      <Box pt={3}>
        <Suspense fallback={<OverLayLoader />}>
          <Routes>
            <Route path="/" element={<Outlet />}>
              <Route
                path="replacements"
                element={
                  <RoleRoute onlyAdmin>
                    <ReplacementsView />
                  </RoleRoute>
                }
              />
              <Route
                path="words"
                element={
                  <RoleRoute onlyAdmin>
                    <WordsSettingsView />
                  </RoleRoute>
                }
              />
              <Route
                path="words/add/:codeId/:language"
                element={
                  <RoleRoute onlyAdmin>
                    <AddKeywordsView />
                  </RoleRoute>
                }
              />
              <Route
                path="replacements/add/:language"
                element={
                  <RoleRoute onlyAdmin>
                    <AddReplacementView />
                  </RoleRoute>
                }
              />
              <Route
                path="related/add_link/:language"
                element={
                  <RoleRoute onlyAdmin>
                    <AddRelatedLinkView />
                  </RoleRoute>
                }
              />
              <Route
                path="complex/add_link/:language"
                element={
                  <RoleRoute onlyAdmin>
                    <AddComplexLinkView />
                  </RoleRoute>
                }
              />
              <Route
                path="mapped/add_link/:language"
                element={<AddMappedLinkView />}
              />
              <Route
                path="related"
                element={
                  <RoleRoute onlyAdmin>
                    <RelatedCodeView />
                  </RoleRoute>
                }
              />
              <Route
                path="complex"
                element={
                  <RoleRoute onlyAdmin>
                    <ComplexCodeView />
                  </RoleRoute>
                }
              />
              <Route path="mapped" element={<MappedCodeView />} />
              <Route
                path="downloads"
                element={
                  <RoleRoute onlyAdmin>
                    <DownloadsView />
                  </RoleRoute>
                }
              />
            </Route>
          </Routes>
        </Suspense>
      </Box>
    </Box>
  );
};

export default DictionariesView;
